import clone from 'lodash/clone';
import debounce from 'lodash/debounce';
import defaultsDeep from 'lodash/defaultsDeep';
import differenceWith from 'lodash/differenceWith';
import each from 'lodash/each';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import has from 'lodash/has';
import includes from 'lodash/includes';
import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import isDate from 'lodash/isDate';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import map from 'lodash/map';
import merge from 'lodash/merge';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import throttle from 'lodash/throttle';
import toPairs from 'lodash/toPairs';
import doMatchType from 'clients/doMatchType';
import filterTypes from 'components/list/Filters/filterMatchTypes';

export default (function () {
    const utils = {
        clone: clone,
        debounce: debounce,
        defaultsDeep: defaultsDeep,
        each: each,
        find: find,
        findIndex: findIndex,
        get: get,
        groupBy: groupBy,
        has: has,
        includes: includes,
        isArray: isArray,
        isBoolean: isBoolean,
        isDate: isDate,
        isEqual: isEqual,
        isFunction: isFunction,
        isNumber: isNumber,
        isObject: isObject,
        isPlainObject: isPlainObject,
        isString: isString,
        isUndefined: isUndefined,
        map: map,
        merge: merge,
        pick: pick,
        throttle: throttle,
        toPairs: toPairs,

        /**
         * Does the users browser support cookies.
         *
         * @returns {boolean}
         */
        canUseCookies: function () {
            try {
                document.cookie = 'cookietest=1';
                let cookiesEnabled = document.cookie.indexOf('cookietest=') !== -1;

                document.cookie = 'cookietest=1; expires=Thu, 01-Jan-1970 00:00:01 GMT';
                return cookiesEnabled;
            } catch (e) {
                return false;
            }
        },

        /**
         * Get the difference between two objects in a new object.
         *
         * @param {Object} previous
         * @param {Object} current
         * @returns {Object}
         */
        diffObjects: function (previous, current) {
            if (!previous) {
                return current;
            }

            return pickBy(current, (value, key) => {
                return previous[key] !== value;
            });
        },

        /**
         * Get the difference between two arrays in a new array.
         *
         * @param {Array} previous
         * @param {Array} current
         * @returns {Array}
         */
        diffArrays: function (previous, current) {
            if (!previous) {
                return current;
            }

            return differenceWith(previous, current, isEqual);
        },

        /**
         * Filter all empty values.
         *
         * @param {Object} obj
         * @todo Loop through the Array and delete all values.
         */
        filterEmptyValues: function (obj) {
            let filteredObj = {};

            each(obj, (value, key) => {
                if (isArray(value)) {
                    filteredObj[key] = value;
                    return;
                }

                if (isObject(value)) {
                    value = utils.filterEmptyValues(value);
                }

                if (utils.isEmpty(value)) {
                    return;
                }

                filteredObj[key] = value;
            });

            return filteredObj;
        },

        /**
         * Is a given value empty?
         *
         * @param {Object} obj - Object to be checked if it's empty.
         * @returns {boolean}
         */
        isEmpty: function (obj) {
            if (obj === '' || obj === null || isUndefined(obj)) {
                return true;
            }

            // Check if it's an empty array
            if (isArray(obj) && obj.length === 0) {
                return true;
            }

            // Check if it's an empty object
            if (isObject(obj)) {
                for (let key in obj) {
                    if (obj.hasOwnProperty(key)) {
                        return false;
                    }
                }

                return true;
            }

            return false;
        },

        /**
         * Is given object a Numeric value?
         *
         * @param {number} number
         * @returns {boolean} Whether given object is a numeric value.
         */
        isNumeric: function (number) {
            return !isNaN(number);
        },

        /**
         * Split a date that is structured like `yyyy-mm-dd hh:mm:ss` into an array.
         *
         * @param {string} dateString - The date.string.
         * @returns {Array}
         */
        _getDateParts: function (dateString) {
            const dateTimeSplit = dateString.split(' ');
            const dateSplit = dateTimeSplit[0].split('-');
            const timeSplit = dateTimeSplit[1].split(':');
            const dateArray = [
                // Year
                dateSplit[0],
                // Month index
                dateSplit[1] - 1,
                // Day
                dateSplit[2],
                // Hours
                timeSplit[0],
                // Minutes
                timeSplit[1],
                // Seconds
                timeSplit[2],
            ];

            return dateArray;
        },

        /**
         * Convert a local DateTime string into an Date Object.
         *
         * @param {string} dateString - The date.string.
         * @param {boolean} isUTC - Is the date string a UTC time?
         * @returns {Object} - A new Date object
         */
        createDateTime: function (dateString, isUTC = false) {
            const dateArray = utils._getDateParts(dateString);

            if (isUTC) {
                return new Date(Date.UTC(...dateArray));
            }

            return new Date(...dateArray);
        },

        /**
         * Convert a DateObject into a locale string with optional options.
         *
         * @param {Date} date - The date.object.
         * @param {Object} [options] - Date.options.
         * @returns {string} - A new Date object
         */
        toDateString: function (date, options = {}) {
            return date.toLocaleString([], options);
        },

        /**
         * Normalizes the payload.
         *
         * @param {Object} payload
         * @returns {Object|Array} result
         */
        normalizePayload: function (payload) {
            let result = {};

            // Loop through the array and normalize the payload.
            each(toPairs(payload), ([
                key,
                val,
            ]) => {
                // We leave files alone due to AOR magic - let there be dragons which allow you to pass, young Frodo.
                if (val instanceof File) {
                    result[key] = val;
                    return;
                }

                if (val === null) {
                    return;
                }

                if (isArray(val)) {
                    each(val, (subValue) => {
                        return subValue !== '' || subValue !== undefined;
                    });
                }

                if (isPlainObject(val)) {
                    val = utils.normalizePayload(val);
                }

                if (isString(val)) {
                    val = val.trim();
                }

                result[key] = val;
            });

            return result;
        },

        /**
         * @param {Object} obj
         */
        buildFilterObject: (obj) => {
            let filters = {};

            each(obj, (objValue, objKey) => {
                const { matchType, value } = objValue;

                if (!value) {
                    return;
                }

                if (isArray(value)) {
                    filters[objKey] = map(value, function (item) {
                        return doMatchType(matchType, item);
                    });

                    return;
                }

                const formattedValue = doMatchType(matchType, value);

                switch (matchType) {
                    case filterTypes.between:
                    case filterTypes.matchAllOf:
                    case filterTypes.matchOneOf:
                        filters[objKey] = [
                            formattedValue,
                        ];
                        break;
                    default:
                        filters[objKey] = formattedValue;
                        break;
                }
            });

            return filters;
        },

        buildQueryString: (params, prefix = '') => {
            const encodedParams = [
                'city',
                'company_name',
                'country',
                'email',
                'last_name',
                'license_plate',
                'name',
                'point_of_interest',
                'title',
                'username',
            ];

            let queryString = [];

            each(params, (value, key) => {
                const formattedKey = prefix ? `${ prefix }[${ key }]` : key;

                if (isArray(value)) {
                    each(value, (arrayValue) => {
                        queryString.push(`${ formattedKey }[]=${ arrayValue }`);
                    });
                } else if (isObject(value)) {
                    queryString.push(utils.buildQueryString(value, formattedKey));
                } else {
                    if (~encodedParams.indexOf(key)) {
                        value = encodeURIComponent(value);
                    }

                    queryString.push(`${ formattedKey }=${ value }`);
                }
            });

            return queryString.join('&');
        },

        /**
         * Adds the params to the base URL with the correct separator.
         *
         * @param {string} baseUrl
         * @param {Object} params
         * @returns {string}
         */
        buildUrl: (baseUrl, params) => {
            if (utils.isEmpty(params)) {
                return baseUrl;
            }

            params = utils.normalizePayload(params);

            return baseUrl + (~baseUrl.indexOf('?') ? '&' : '?') + utils.buildQueryString(params);
        },

        /**
         * The mapToObject methods converts an Array to an Object.
         *
         * @param {Array} arr - the array.
         * @param {string} callback - Function which is called on every entry, returns the key for the object.
         * @returns {Object}
         */
        mapToObject: (arr, callback) => {
            let result = {};

            arr.forEach((value) => {
                result[callback(value)] = value;
            });

            return result;
        },

        only: function (obj, keys) {
            let result = {};

            each(obj, function (value, key) {
                if (~keys.indexOf(key)) {
                    result[key] = value;
                }
            });

            return result;
        },

        /**
         * The reverse() method creates a new Array|Object with the results in reversed order.
         *
         * @param {Array|Object} dataSet - Array|Object to traverse through.
         */
        reverse: (dataSet) => {
            if (isArray(dataSet)) {
                return clone(dataSet).reverse();
            }

            let result = {};
            const keys = Object.keys(dataSet).reverse();

            each(keys, function (key) {
                result[key] = dataSet[key];
            });

            return result;
        },

        /**
         * Generate a new unique identifier.
         *
         * @returns {string}
         */
        uuid: () => {
            let dt = new Date().getTime();

            const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                const r = (dt + (Math.random() * 16)) % 16 | 0;

                dt = Math.floor(dt / 16);

                return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
            });

            return uuid;
        },

        /**
         * Check if the user's browser is too outdated for dashboard support.
         *
         * @returns {boolean}
         */
        isOutdatedBrowser: function () {
            if (navigator.userAgent.indexOf('MSIE ') > -1) {
                // IE 10 and older :)
                return true;
            }

            return false;
        },

        /**
         * Change the first character of a string to an capital letter.
         *
         * @param {string} str
         * @returns {string}
         */
        firstCharToCamelCase: (str) => {
            if (!isString(str)) {
                return;
            }

            return str.charAt(0).toUpperCase() + str.slice(1);
        },

        /**
         * Change the first character of a string to an lowercase letter.
         *
         * @param {string} str
         * @returns {string}
         */
        firstCharToLowerCase: (str) => {
            if (!isString(str)) {
                return;
            }

            return str.charAt(0).toLowerCase() + str.slice(1);
        },

        /**
         * Replaces underscores and dashes with space.
         *
         * @param {string} str
         * @returns {string}
         */
        formatPropertyName: (str) => {
            if (!isString(str)) {
                return;
            }

            return str.replace(/[-_]/, ' ');
        },

        /**
         * This function combines the amount and the currency to a locale string.
         *
         * @param {string | number } amount
         * @param {string} currency
         * @param {string} locale
         * @returns {string}
         */
        formatPrice: function (amount, currency, locale = undefined) {
            return parseFloat(amount).toLocaleString(locale, { style: 'currency', currency: currency ? currency : 'EUR' });
        },

        /**
         * This will combine the first and the last name when available.
         *
         * @param {string} firstName
         * @param {string} lastName
         *
         * @returns {string}
         */
        fullName: function (firstName, lastName) {
            firstName = firstName ? utils.firstCharToCamelCase(firstName) : null;
            lastName = lastName ? utils.firstCharToCamelCase(lastName) : null;

            if (lastName) {
                return `${ firstName } ${ lastName }`;
            }

            return firstName;
        },

        /**
         * Returns a sorted array from passed object.
         *
         * @param {Object} object
         * @param {string | number} key
         * @param {string} type
         */
        sort: function (object, key, type) {
            if (this.isEmpty(object) || !this.isObject(object)) {
                return;
            }

            if (!key || !type) {
                return object;
            }

            return Object.values(object).sort((objectOne, objectTwo) => {
                if (!objectOne.hasOwnProperty(key) || !objectTwo.hasOwnProperty(key)) {
                    return null;
                }

                if (type === 'desc') {
                    return objectTwo[key] - objectOne[key];
                }

                return objectOne[key] - objectTwo[key];
            });
        },

        /**
         * This will convert the first and last names in initials
         *
         * @param {string} firstName
         * @param {string} lastName
         *
         * @returns {string}
         */
        nameInitials: function (firstName, lastName) {
            let initials = '';

            if (firstName) {
                initials += firstName.charAt(0);
            }

            if (lastName) {
                initials += lastName.charAt(0);
            }

            if (!initials) {
                // Refers the the English "you"
                initials += 'U';
            }

            return initials;
        },

        /**
         * This will convert a string into a number.
         * reference: https://www.geeksforgeeks.org/how-to-create-hash-from-string-in-javascript/
         *
         * @param {string} string
         *
         * @returns {number}
         */
        getHashCode: function (string) {
            if (string.length === 0) {
                return 0;
            }

            let hash = 0;

            for (let i = 0; i < string.length; i++) {
                const char = string.charCodeAt(i);

                hash = ((hash << 5) - hash) + char;
                hash = hash & hash;
            }

            return hash;
        },

        /**
         * Returns number difference from parameters.
         *
         * @param {string|number} stringA
         * @param {string|number} stringB
         *
         * @returns {number}
         */
        compareStrings: function (stringA, stringB) {
            if (!isString(stringA) && !isString(stringB)) {
                return stringA - stringB;
            }

            return this.getHashCode(stringA) - this.getHashCode(stringB);
        },

        /**
         * This will add optional to the label
         *
         * @param {string} label
         * @param {func} translate
         *
         * @returns {string}
         */
        optionalLabel: function (label, translate) {
            return `${ label } ${ translate('fields.optional') }`;
        },

        /**
         * This will update '' keys to null from object
         *
         * @param {Object} object
         *
         * @returns {Object}
         */
        cleanObjectKeys: function (object) {
            for (const key in object) {
                if (!object[key]) {
                    object[key] = null;
                }
            }

            return object;
        },

        /**
         * Checks if key exists is nested in object.
         *
         * @param {Object} object
         * @param {string|number} key
         * @returns {boolean}
         */
        hasKey: function (object, key) {
            return Object.keys(object).indexOf(key) > -1;
        },

        /**
         * Returns formated two-digit string.
         *
         * @param {number} number
         *
         * @returns {string}
         */
        addZero: function (number) {
            return (`0${ number }`).slice(-2);
        },

        /**
         * Will filter out the keys from the given object
         *
         * @param {Object} object
         * @param {Function} predicate
         *
         * @returns {Object}
         */
        fliterOutObjectKeys: function (object, predicate) {
            return Object.keys(object)
                .filter(predicate)
                .reduce((cur, key) => {
                    return Object.assign(cur, { [key]: object[key] });
                }, {});
        },
    };

    return utils;
})();



// WEBPACK FOOTER //
// ./src/helpers/utils.js